import classNames from "classnames";
import CategoryTree from "component/category/CategoryTree";
import Element from "component/design-system/react-scroll/components/Element";
import NbcFilterImage from "component/product/extended-definition/NbcFilterImage";
import { MAKE_MODEL_YEAR, MAKE_YEAR_MODEL, YEAR_MAKE_MODEL } from "component/vehicle-form/constants";
import mergeOptions from "merge-options";
import { FILTER_CONFIG } from "modules/category/constants/filter-config";
import { useTranslation } from "next-i18next";
import { isFunction } from "util/helper/isFunction";
import { pick } from "util/lodash/pick";
import { scrollTo } from "util/scroll-helper";
import FilterLabel from "../FilterLabel";
import FilterSidebarMenu from "../FilterSidebarMenu";
import ProductCheckBoxFilter from "../product-checkbox-filter/ProductCheckboxFilter";
import ProductFilterVehicleComponent from "../product-filter-vehicle.component";
import TireSizeSummary from "../tire-size-summary.component";
import styles from "./product-filter.module.scss";

function renderAllLabel(items: any, label: any) {
  if (!items) {
    return 0;
  }
  const total = items.reduce((prev: any, curr: any) => prev + curr.count, 0);
  return (
    <>
      {label}

      <span className="result-count">
        {` (${total})`}
      </span>
    </>
  );
}

const getSelectionOrder = ({
  make,
  year,
}: any) => {
  if (make) {
    return year ? MAKE_YEAR_MODEL : MAKE_MODEL_YEAR;
  }
  return YEAR_MAKE_MODEL;
};

const defaultParams = {
  manufacturers: {
    allLabel: (items: any) => renderAllLabel(items, "ALL MANUFACTURERS"),
  },
  prices: {
    allLabel: (items: any) => renderAllLabel(items, "ALL PRICES"),
  },
};

function ProductFilter(props: any) {
  const {
    isMobile, data, appliedFilters, tiersWithUrls, categoryAggregation, isCategoryOpen,
    customDropdown, productFilter, scrollAll = true, isModal, garageTitle = null, className, maxFilters, params: productFilterParams = defaultParams,
    nbcFilterableAttributes,
  } = props;

  const { t } = useTranslation("category");

  const handleToggleScroll = (filterKey: any, scrolling: any) => {
    if (scrolling && !isMobile) {
      scrollTo(`anchor_filter_${filterKey}`, {
        duration: 500,
        delay: 100,
        smooth: true,
        offset: -130,
      });
    }
  };

  const renderFilter = (filterKey: any, index: any, mergeFilterConfig: any) => {
    const params = mergeFilterConfig;

    if (!data[filterKey] && params[filterKey].forceRender !== true) {
      return null;
    }

    const { type, sorter, ...rest } = params[filterKey];
    const filterParams = {
      appliedFilters,
      items: Array.isArray(data[filterKey]) && sorter ? data[filterKey].sort(sorter) : data[filterKey],
      itemLabel: (item: any) => (
        <FilterLabel
          item={item}
          showCount={type !== "nbcCheckbox"}
          nbcImage={type === "nbcCheckbox" && (
            <NbcFilterImage filterKey={filterKey} filterValue={item.bucket} size={25} hideCommonLabel />
          )}
        />
      ),
      tiersWithUrls,
      url: data.url || "",
      productFilter,
      ...rest,
    };

    // hide filter options when all items have product count zero
    if (!["engines"].includes(filterKey) && filterParams?.items
      && Array.isArray(filterParams.items)
      && type !== "nbcCheckbox" && filterParams.items.filter((_: any) => _.count > 0).length === 0) {
      return null;
    }

    let enabled;
    if (isFunction(filterParams.enabled)) {
      enabled = filterParams.enabled.call(this, filterKey);
    } else {
      enabled = filterParams.enabled;
    }

    if (enabled === false) {
      return null;
    }

    if (!Object.prototype.hasOwnProperty.call(filterParams, "scrollable")) {
      filterParams.scrollable = scrollAll;
    }

    if (
      (type === "list" || type === "checkbox")
      && !Object.prototype.hasOwnProperty.call(filterParams, "onToggleScroll")
    ) {
      filterParams.onToggleScroll = handleToggleScroll;
    }

    if (Array.isArray(filterParams.items) && filterParams.items.length === 0) {
      return null;
    }
    let component;
    switch (type) {
    case "list":
      component = categoryAggregation && categoryAggregation.tier0s ? (
        <FilterSidebarMenu
          title={t("Categories")}
          isDefaultexpanded={isCategoryOpen}
          className={styles.category_filter}
          renderList={() => (
            <CategoryTree
              tier0s={categoryAggregation?.tier0s}
              appliedFilters={appliedFilters}
              tiersWithUrls={tiersWithUrls}
              customScroll
            />
          )}
        />
      ) : (null);
      break;
    case "checkbox":
      component = (
        <ProductCheckBoxFilter
          key={`${index}-1`}
          index={index}
          {...filterParams}
          isModal={isModal}
          customDropdown={customDropdown}
        />
      );
      break;
    case "nbcCheckbox":
      component = (
        <ProductCheckBoxFilter
          key={`${index}-1`}
          index={index}
          {...filterParams}
          isModal={isModal}
          customDropdown={customDropdown}
        />
      );
      break;
    case "vehicle":
      component = (
        <ProductFilterVehicleComponent
          key={`${index}-1`}
          {...filterParams}
          items={pick(filterParams.appliedFilters, ["make", "model", "year"])}
          selectionOrder={getSelectionOrder(pick(filterParams.appliedFilters, ["make", "model", "year"]))}
          category={pick(filterParams.appliedFilters, ["tier0", "tier1", "tier2", "tier3"])}
          garageTitle={garageTitle}
        />
      );
      break;
    case "tireSummary":
      component = (
        <TireSizeSummary
          key={`${index}-1`}
          currentSizes={data.sizes}
        />
      );
      break;
    default:
      component = null;
    }

    return [

      <Element key={`${index}-0`} name={`anchor_filter_${filterKey}`} />,
      component,
    ];
  };

  const nbcFilterableParams = {};
  nbcFilterableAttributes.forEach(({ attributeName, attributeTitle }, idx) => {
    nbcFilterableParams[attributeName] = {
      type: "nbcCheckbox",
      order: 15 + idx,
      title: attributeTitle,
      filterKey: attributeName,
      multiple: true,
    };
  });

  const params = mergeOptions({}, isMobile ? {
    category: {
      type: "list",
      order: 1,
      title: "Category",
      enabled: true,
      forceRender: true,
    },
  } : {}, FILTER_CONFIG, defaultParams, productFilterParams, nbcFilterableParams);

  let sortedFilters = Object.keys(params)
    .sort((a, b) => {
      try {
        return params[a].order - params[b].order;
      } catch (e) {
        return 0;
      }
    })
    .filter((filterKey) => {
      const filterOptions = data[filterKey];
      return filterKey === "category" || (filterOptions && Array.isArray(filterOptions) && filterOptions.length > 0);
    });

  const isApparel = productFilter.tier2 === "Shirts";
  if (isApparel) {
    sortedFilters = sortedFilters.filter((filterKey) => !["fitments", "manufacturers"].includes(filterKey));
  }

  return (
    <div className={classNames(styles.product_filter, className, {
      "flex-column": isModal,
    })}
    >
      {sortedFilters
        .slice(0, isModal ? sortedFilters.length : maxFilters || sortedFilters.length)
        .map((filterKey, index) => renderFilter(filterKey, index, params))}
    </div>
  );
}

export default ProductFilter;
